import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { Alert } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import {
  Instrument,
  EndUsePhotoGroup,
  getInstrumentsPaginated,
  getInstrumentById,
  addInstrument,
  updateInstrument,
  deleteInstrument,
  type ReservationRules,
  type TimeSlot
} from '../services/api';
import { knowledgeBaseService, type KnowledgeBase } from '../services/knowledgeBase/knowledgeBaseService';
import { parseInstrumentUsageKbRef } from '../utils/instrumentUsageKb';
import { LimsNavIcon } from '../components/LimsNavIcon';
import ResponsiblePersonMultiSelect, { ResponsibleOptionUser } from '../components/ResponsiblePersonMultiSelect';
import { useCanAccessInstrumentStock } from '../hooks/useCanAccessInstrumentStock';
import { resolveStockItemImageSrc } from '../utils/resolveUploadAssetUrl';
import { DEFAULT_INSTRUMENT_IMAGE } from '../constants/defaultImages';
import './MemberManagement.css';
import './InstrumentStock.modal.css';

const PAGE_SIZE = 20;

const parseResponsiblePersons = (value?: string): string[] => {
  if (!value) return [];
  return value
    .split(/[、,，;\s]+/)
    .map(item => item.trim())
    .filter(Boolean);
};

const formatMaintenanceDueDate = (startDate?: string | null, days?: number | null): string => {
  if (!startDate || !days || days < 1) return '';
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('zh-CN');
};

const DEFAULT_TIME_SLOTS: TimeSlot[] = [
  { dayOfWeek: 0, startTime: '09:00', endTime: '18:00', isEnabled: false },
  { dayOfWeek: 1, startTime: '09:00', endTime: '18:00', isEnabled: true },
  { dayOfWeek: 2, startTime: '09:00', endTime: '18:00', isEnabled: true },
  { dayOfWeek: 3, startTime: '09:00', endTime: '18:00', isEnabled: true },
  { dayOfWeek: 4, startTime: '09:00', endTime: '18:00', isEnabled: true },
  { dayOfWeek: 5, startTime: '09:00', endTime: '18:00', isEnabled: true },
  { dayOfWeek: 6, startTime: '09:00', endTime: '18:00', isEnabled: false }
];

function normalizeReservationRules(fromServer?: Partial<ReservationRules> | null): ReservationRules {
  const slots =
    fromServer?.availableTimeSlots && fromServer.availableTimeSlots.length > 0
      ? fromServer.availableTimeSlots
      : DEFAULT_TIME_SLOTS;
  return {
    maxDuration: fromServer?.maxDuration ?? 240,
    minDuration: fromServer?.minDuration ?? 30,
    advanceBookingDays: fromServer?.advanceBookingDays ?? 7,
    requiresApproval: fromServer?.requiresApproval ?? false,
    maxConcurrentUsers: fromServer?.maxConcurrentUsers ?? 1,
    availableTimeSlots: slots,
    endUseStatusEnabled: fromServer?.endUseStatusEnabled ?? false,
    endUsePhotoGroups: fromServer?.endUsePhotoGroups ?? []
  };
}

const InstrumentStock: React.FC = () => {
  const { isStockAdmin } = useCanAccessInstrumentStock();
  const [data, setData] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<Instrument | null>(null);
  const [users, setUsers] = useState<ResponsibleOptionUser[]>([]);
  const [useDefaultTimeSlots, setUseDefaultTimeSlots] = useState<boolean>(false);
  const [selectedResponsiblePersons, setSelectedResponsiblePersons] = useState<string[]>([]);
  const [selectedAllowedUsers, setSelectedAllowedUsers] = useState<string[]>([]);
  const [form, setForm] = useState<Partial<Instrument>>({
    name: '',
    model: '',
    manufacturer: '',
    location: '',
    status: '可用',
    category: '',
    description: '',
    purchaseDate: '',
    warrantyExpiry: '',
    maintenanceCycleDays: null,
    responsiblePerson: '',
    allowedUsers: '',
    image: '',
    usageInstructionKnowledgeBaseId: undefined,
    reservationRules: normalizeReservationRules(null)
  });
  const [usageKbPickerOpen, setUsageKbPickerOpen] = useState(false);
  const [usageKbKeyword, setUsageKbKeyword] = useState('');
  const [usageKbList, setUsageKbList] = useState<KnowledgeBase[]>([]);
  const [usageKbLoading, setUsageKbLoading] = useState(false);
  const usageKbSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [previewModal, setPreviewModal] = useState(false);
  const [previewImg, setPreviewImg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('全部');
  const [page, setPage] = useState(1);
  const [maintainModal, setMaintainModal] = useState(false);
  const [maintainContent, setMaintainContent] = useState('');
  const [maintainTarget, setMaintainTarget] = useState<Instrument | null>(null);
  const [alertMsg, setAlertMsg] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'danger'>('success');
  /** 列表为轻量字段，编辑前拉取完整详情 */
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [maintenanceCycleMode, setMaintenanceCycleMode] = useState<'none' | 'custom'>('none');
  const [maintenanceCycleCustomDays, setMaintenanceCycleCustomDays] = useState(30);

  // 获取仪器列表（分页）
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getInstrumentsPaginated({
        page,
        pageSize: PAGE_SIZE,
        managedOnly: !isStockAdmin
      });
      if (response.success && response.data) {
        setData(response.data.items);
        setTotal(response.data.total);
      } else {
        setAlertMsg(response.error || '获取仪器列表失败');
        setAlertType('danger');
      }
    } catch (error) {
      setAlertMsg('网络错误，请稍后重试');
      setAlertType('danger');
    } finally {
      setLoading(false);
    }
  };

  // 获取用户列表
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/users', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const userData = await response.json();
      if (Array.isArray(userData)) {
        setUsers(userData.filter((u: ResponsibleOptionUser) => u.role !== '超级管理员'));
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchUsers();
  }, [page]);

  const fetchUsageKbOptions = useCallback(async (keyword: string) => {
    setUsageKbLoading(true);
    try {
      const { knowledgeBases } = await knowledgeBaseService.getList({
        page: 1,
        limit: 40,
        keyword: keyword.trim() || undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });
      setUsageKbList(knowledgeBases);
    } catch (e) {
      console.error(e);
      setUsageKbList([]);
    } finally {
      setUsageKbLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!usageKbPickerOpen) return;
    if (usageKbSearchTimer.current) clearTimeout(usageKbSearchTimer.current);
    usageKbSearchTimer.current = setTimeout(() => {
      void fetchUsageKbOptions(usageKbKeyword);
    }, 320);
    return () => {
      if (usageKbSearchTimer.current) clearTimeout(usageKbSearchTimer.current);
    };
  }, [usageKbPickerOpen, usageKbKeyword, fetchUsageKbOptions]);

  useEffect(() => {
    if (editing) {
      setForm({
        ...editing,
        reservationRules: normalizeReservationRules(editing.reservationRules)
      });
      setUploadPreview(editing.image || '');
      setSelectedResponsiblePersons(parseResponsiblePersons(editing.responsiblePerson || ''));
      setSelectedAllowedUsers(parseResponsiblePersons(editing.allowedUsers || ''));
      const cycleDays = editing.maintenanceCycleDays;
      if (cycleDays && cycleDays > 0) {
        setMaintenanceCycleMode('custom');
        setMaintenanceCycleCustomDays(cycleDays);
      } else {
        setMaintenanceCycleMode('none');
        setMaintenanceCycleCustomDays(30);
      }
      const hasCustom = Array.isArray(editing.reservationRules?.availableTimeSlots) && editing.reservationRules!.availableTimeSlots.length > 0;
      setUseDefaultTimeSlots(!hasCustom);
    } else {
      // 新增时，使用默认值
      setForm({
        name: '',
        model: '',
        manufacturer: '',
        location: '',
        status: '可用',
        category: '',
        description: '',
        purchaseDate: '',
        warrantyExpiry: '',
        maintenanceCycleDays: null,
        responsiblePerson: '',
        allowedUsers: '',
        image: '',
        usageInstructionKnowledgeBaseId: undefined,
        reservationRules: normalizeReservationRules(null)
      });
      setUploadPreview('');
      setSelectedResponsiblePersons([]);
      setSelectedAllowedUsers([]);
      setUseDefaultTimeSlots(false);
      setMaintenanceCycleMode('none');
      setMaintenanceCycleCustomDays(30);
    }
    setUsageKbPickerOpen(false);
    setUsageKbKeyword('');
  }, [editing, show]);

  // 图片上传
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => {
        setUploadPreview(ev.target?.result as string);
        setForm(f => ({ ...f, image: ev.target?.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!form.name) {
      setAlertMsg('请填写仪器名称');
      setAlertType('danger');
      return;
    }
    if (!isStockAdmin && !editing) {
      setAlertMsg('无权限新增仪器');
      setAlertType('danger');
      return;
    }

    try {
      const cleanedForm: any = { ...form };
      if (!isStockAdmin) {
        delete cleanedForm.responsiblePerson;
      }
      delete cleanedForm.specifications;
      delete cleanedForm.department;
      delete cleanedForm.qrCode;
      delete cleanedForm.maintenanceSchedule;
      if (cleanedForm.reservationRules) {
        cleanedForm.reservationRules = normalizeReservationRules(cleanedForm.reservationRules);
      }
      if (useDefaultTimeSlots && cleanedForm.reservationRules) {
        const rr = { ...cleanedForm.reservationRules };
        delete rr.availableTimeSlots;
        cleanedForm.reservationRules = rr;
      }

      const kbRaw = cleanedForm.usageInstructionKnowledgeBaseId as unknown;
      if (kbRaw && typeof kbRaw === 'object' && '_id' in (kbRaw as object)) {
        cleanedForm.usageInstructionKnowledgeBaseId = String((kbRaw as { _id: string })._id);
      }
      if (!cleanedForm.usageInstructionKnowledgeBaseId) {
        cleanedForm.usageInstructionKnowledgeBaseId = null;
      } else {
        cleanedForm.usageInstructions = '';
      }
      if (!cleanedForm.usageInstructionKnowledgeBaseId) {
        delete cleanedForm.usageInstructions;
      }

      if (maintenanceCycleMode === 'none') {
        cleanedForm.maintenanceCycleDays = null;
      } else {
        const days = Number(maintenanceCycleCustomDays);
        if (!Number.isFinite(days) || days < 1) {
          setAlertMsg('维护周期天数须为大于 0 的整数');
          setAlertType('danger');
          return;
        }
        cleanedForm.maintenanceCycleDays = Math.floor(days);
      }

      let response;
      if (editing) {
        response = await updateInstrument(editing._id!, cleanedForm);
      } else {
        response = await addInstrument(cleanedForm);
      }
      
      if (response.success) {
        setAlertMsg(editing ? '仪器更新成功' : '仪器添加成功');
        setAlertType('success');
        await fetchData();
        setShow(false);
        setEditing(null);
      } else {
        setAlertMsg(response.error || '操作失败');
        setAlertType('danger');
      }
    } catch (error) {
      setAlertMsg('网络错误，请稍后重试');
      setAlertType('danger');
    }
  };

  const handleEdit = async (item: Instrument) => {
    if (!item._id) return;
    setEditLoadingId(item._id);
    try {
      const res = await getInstrumentById(item._id);
      if (res.success && res.data) {
        setEditing(res.data);
        setShow(true);
      } else {
        setAlertMsg(res.error || '加载仪器详情失败');
        setAlertType('danger');
      }
    } catch {
      setAlertMsg('网络错误，无法加载仪器详情');
      setAlertType('danger');
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('确定要删除这个仪器吗？')) {
      try {
        const response = await deleteInstrument(id);
        if (response.success) {
          setAlertMsg('仪器删除成功');
          setAlertType('success');
          await fetchData();
        } else {
          setAlertMsg(response.error || '删除失败');
          setAlertType('danger');
        }
      } catch (error) {
        setAlertMsg('网络错误，请稍后重试');
        setAlertType('danger');
      }
    }
  };

  const handleMaintain = (item: Instrument) => {
    setMaintainTarget(item);
    setMaintainContent('');
    setMaintainModal(true);
  };
  const handleSendMaintain = () => {
    setMaintainModal(false);
    setAlertMsg(`已发送维护提醒给负责人：${maintainTarget?.responsiblePerson}`);
    setTimeout(() => setAlertMsg(''), 3000);
  };

  /** 放弃未保存的修改，恢复为打开弹窗时的状态 */
  const handleDiscardInstrumentForm = () => {
    if (editing) {
      setForm({
        ...editing,
        reservationRules: normalizeReservationRules(editing.reservationRules)
      });
      setUploadPreview(editing.image || '');
      setSelectedResponsiblePersons(parseResponsiblePersons(editing.responsiblePerson || ''));
      setSelectedAllowedUsers(parseResponsiblePersons(editing.allowedUsers || ''));
      const hasCustom =
        Array.isArray(editing.reservationRules?.availableTimeSlots) &&
        editing.reservationRules!.availableTimeSlots!.length > 0;
      const cycleDays = editing.maintenanceCycleDays;
      if (cycleDays && cycleDays > 0) {
        setMaintenanceCycleMode('custom');
        setMaintenanceCycleCustomDays(cycleDays);
      } else {
        setMaintenanceCycleMode('none');
        setMaintenanceCycleCustomDays(30);
      }
      setUseDefaultTimeSlots(!hasCustom);
    } else {
      setForm({
        name: '',
        model: '',
        manufacturer: '',
        location: '',
        status: '可用',
        category: '',
        description: '',
        purchaseDate: '',
        warrantyExpiry: '',
        maintenanceCycleDays: null,
        responsiblePerson: '',
        allowedUsers: '',
        image: '',
        usageInstructionKnowledgeBaseId: undefined,
        reservationRules: normalizeReservationRules(null)
      });
      setUploadPreview('');
      setSelectedResponsiblePersons([]);
      setSelectedAllowedUsers([]);
      setUseDefaultTimeSlots(false);
      setMaintenanceCycleMode('none');
      setMaintenanceCycleCustomDays(30);
    }
    setUsageKbPickerOpen(false);
    setUsageKbKeyword('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 预约规则相关函数
  const addEndUsePhotoGroup = () => {
    const key = `eu_${Date.now()}`;
    const groups = [...(form.reservationRules?.endUsePhotoGroups || [])];
    groups.push({ key, label: '', required: true, maxPhotos: 3 });
    updateReservationRule('endUsePhotoGroups', groups);
  };

  const updateEndUsePhotoGroup = (index: number, patch: Partial<EndUsePhotoGroup>) => {
    const groups = [...(form.reservationRules?.endUsePhotoGroups || [])];
    groups[index] = { ...groups[index], ...patch };
    updateReservationRule('endUsePhotoGroups', groups);
  };

  const removeEndUsePhotoGroup = (index: number) => {
    const groups = (form.reservationRules?.endUsePhotoGroups || []).filter((_, i) => i !== index);
    updateReservationRule('endUsePhotoGroups', groups);
  };

  const updateReservationRule = (field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      reservationRules: {
        ...prev.reservationRules!,
        [field]: value
      }
    }));
  };

  const updateTimeSlot = (dayOfWeek: number, field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      reservationRules: {
        ...prev.reservationRules!,
        availableTimeSlots: prev.reservationRules!.availableTimeSlots.map(slot =>
          slot.dayOfWeek === dayOfWeek ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return days[dayOfWeek];
  };

  // 搜索和筛选（前端过滤当前页数据）
  const categories = Array.from(new Set(data.map(d => d.category).filter(Boolean)));
  let filtered = data.filter(item => {
    const matchSearch = !search || item.name.includes(search) || 
                       (item.model && item.model.includes(search)) || 
                       (item.location && item.location.includes(search));
    const matchCategory = filterType === '全部' || item.category === filterType;
    return matchSearch && matchCategory;
  });
  // 注意：如果使用搜索/过滤，应该重新请求后端，但为了简化，这里先在前端过滤
  // 实际生产环境建议将搜索和过滤移到后端

  const statusPillClass = (s: Instrument['status']) => {
    if (s === '可用') return 'mm-stock-pill mm-stock-pill--ok';
    if (s === '使用中') return 'mm-stock-pill mm-stock-pill--run';
    if (s === '维护中' || s === '故障') return 'mm-stock-pill mm-stock-pill--warn';
    return 'mm-stock-pill mm-stock-pill--mute';
  };

  // 翻页
  const handlePageChange = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>仪器</span>
        <span className="mm-crumb-sep">/</span>
        <span>库存管理</span>
      </nav>

      {alertMsg && (
        <Alert
          type={alertType === 'success' ? 'success' : 'error'}
          closable
          showIcon
          onClose={() => setAlertMsg('')}
          message={alertMsg}
          className="mm-banner"
        />
      )}

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">仪器库存管理</h1>
          <p className="mm-subtitle">
            {isStockAdmin
              ? '维护设备档案、状态与预约规则，与仪器展示列表数据一致。'
              : '您作为负责人可维护所负责设备的档案、状态与预约规则（不可修改负责人设置）。'}
          </p>
        </div>
        {isStockAdmin && (
          <button type="button" className="mm-btn-primary" onClick={() => { setEditing(null); setShow(true); }}>
            <LimsNavIcon name="add" sizePx={20} />
            新增仪器
          </button>
        )}
      </header>

      <div className="mm-table-shell">
        <div className="mm-table-toolbar">
          <div className="mm-filter-row">
            <span className="mm-filter-label">分类：</span>
            <div className="mm-filter-pills">
              <button
                type="button"
                className={`mm-pill ${filterType === '全部' ? 'is-active' : ''}`}
                onClick={() => { setFilterType('全部'); setPage(1); }}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`mm-pill ${filterType === c ? 'is-active' : ''}`}
                  onClick={() => { setFilterType(c || '全部'); setPage(1); }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="mm-search">
            <LimsNavIcon name="search" sizePx={18} className="mm-search-icon" />
            <input
              type="search"
              className="mm-search-input"
              placeholder="名称、型号、位置…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              aria-label="搜索仪器"
            />
          </div>
          <div className="mm-toolbar-meta">
            第 <strong>{page}</strong> / {totalPages} 页 · 本页 <strong>{filtered.length}</strong> 条 · 共 {total} 台
            <div className="mm-mini-nav">
              <button type="button" aria-label="上一页" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                <LimsNavIcon name="chevron_left" sizePx={20} />
              </button>
              <button
                type="button"
                aria-label="下一页"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                <LimsNavIcon name="chevron_right" sizePx={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="mm-sheet-scroll">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">加载中...</p>
            </div>
          ) : (
            <table className="mm-sheet">
              <thead>
                <tr>
                  <th>图片</th>
                  <th>仪器名称</th>
                  <th>分类</th>
                  <th>型号</th>
                  <th>制造商</th>
                  <th>位置</th>
                  <th>负责人</th>
                  <th style={{ textAlign: 'center' }}>状态</th>
                  <th>购买日期</th>
                  <th>保修到期</th>
                  <th className="mm-th-actions">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="mm-empty">
                      暂无匹配的仪器
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => (
                    <tr key={item._id} className="mm-row">
                      <td>
                        <img
                          className="mm-instrument-thumb"
                          src={resolveStockItemImageSrc(item.image, DEFAULT_INSTRUMENT_IMAGE)}
                          alt=""
                          onClick={() => { setPreviewImg(resolveStockItemImageSrc(item.image, DEFAULT_INSTRUMENT_IMAGE)); setPreviewModal(true); }}
                        />
                      </td>
                      <td>
                        <p className="mm-identity-name mb-0">{item.name}</p>
                      </td>
                      <td>{item.category || '—'}</td>
                      <td>{item.model || '—'}</td>
                      <td>{item.manufacturer || '—'}</td>
                      <td>{item.location || '—'}</td>
                      <td>{item.responsiblePerson || '—'}</td>
                      <td>
                        <div className="mm-status">
                          <span className={statusPillClass(item.status)}>{item.status}</span>
                        </div>
                      </td>
                      <td>{item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : '—'}</td>
                      <td>{item.warrantyExpiry ? new Date(item.warrantyExpiry).toLocaleDateString() : '—'}</td>
                      <td>
                        <div className="mm-actions">
                          <button
                            type="button"
                            className="mm-icon-action"
                            aria-label="编辑"
                            disabled={editLoadingId === item._id}
                            onClick={() => void handleEdit(item)}
                          >
                            {editLoadingId === item._id ? (
                              <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                              <LimsNavIcon name="edit_note" sizePx={22} />
                            )}
                          </button>
                          {isStockAdmin && (
                            <button type="button" className="mm-icon-action mm-icon-action--danger" aria-label="删除" onClick={() => handleDelete(item._id!)}>
                              <LimsNavIcon name="delete" sizePx={22} />
                            </button>
                          )}
                          {isStockAdmin && (
                            <button type="button" className="mm-icon-action" aria-label="维护提醒" onClick={() => handleMaintain(item)}>
                              <LimsNavIcon name="build" sizePx={22} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 大图预览弹窗 */}
      <Modal show={previewModal} onHide={() => setPreviewModal(false)} centered>
        <Modal.Body style={{ textAlign: 'center' }}>
          <img src={previewImg} alt="instrument-large" style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 8 }} />
        </Modal.Body>
      </Modal>
      <Modal show={maintainModal} onHide={() => setMaintainModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>设备维护提醒</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>维护内容</Form.Label>
              <Form.Control as="textarea" rows={3} value={maintainContent} onChange={e => setMaintainContent(e.target.value)} placeholder="请填写维护内容，如：定期保养、更换配件等" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMaintainModal(false)}>取消</Button>
          <Button variant="primary" onClick={handleSendMaintain} disabled={!maintainContent.trim()}>确认提醒</Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        dialogClassName="ins-edit-modal__dialog"
        contentClassName="ins-edit-modal__content"
        backdropClassName="ins-edit-modal__backdrop"
      >
        <Modal.Body className="ins-edit-modal__body">
          <header className="ins-edit-modal__header">
            <div className="ins-edit-modal__header-left">
              <div className="ins-edit-modal__header-icon" aria-hidden>
                <LimsNavIcon name="precision_manufacturing" sizePx={26} style={{ fontVariationSettings: "'FILL' 1" }} />
              </div>
              <div>
                <h2 className="ins-edit-modal__title">{editing ? '编辑仪器' : '添加仪器'}</h2>
                <p className="ins-edit-modal__subtitle">
                  {editing ? '编辑系统参数与说明文档' : '填写设备档案与预约规则'}
                </p>
              </div>
            </div>
            <button type="button" className="ins-edit-modal__close" onClick={() => setShow(false)} aria-label="关闭">
              <LimsNavIcon name="close" sizePx={22} />
            </button>
          </header>

          <div className="ins-edit-modal__scroll">
            <Form>
              <section className="ins-edit-modal__section">
                <div className="ins-edit-modal__section-head">
                  <span className="ins-edit-modal__section-bar" aria-hidden />
                  <h3 className="ins-edit-modal__section-title">基本属性</h3>
                </div>
                <div className="ins-edit-modal__grid2">
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">仪器名称 *</Form.Label>
                    <Form.Control
                      className="ins-edit-modal__control"
                      value={form.name || ''}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">分类</Form.Label>
                    <Form.Control
                      className="ins-edit-modal__control"
                      value={form.category || ''}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">型号</Form.Label>
                    <Form.Control
                      className="ins-edit-modal__control"
                      value={form.model || ''}
                      onChange={(e) => setForm({ ...form, model: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">制造商</Form.Label>
                    <Form.Control
                      className="ins-edit-modal__control"
                      value={form.manufacturer || ''}
                      onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field ins-edit-modal__field--full">
                    <Form.Label className="ins-edit-modal__label">位置</Form.Label>
                    <div className="ins-edit-modal__input-icon-wrap">
                      <LimsNavIcon name="location_on" sizePx={20} className="ins-edit-modal__input-icon" />
                      <Form.Control
                        className="ins-edit-modal__control ins-edit-modal__control--pl"
                        value={form.location || ''}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                      />
                    </div>
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">状态</Form.Label>
                    <div className="ins-edit-modal__select-wrap">
                      <Form.Select
                        className="ins-edit-modal__control ins-edit-modal__control--select"
                        value={form.status || '可用'}
                        onChange={(e) => setForm({ ...form, status: e.target.value as Instrument['status'] })}
                      >
                        <option value="可用">可用</option>
                        <option value="使用中">使用中</option>
                        <option value="维护中">维护中</option>
                        <option value="故障">故障</option>
                        <option value="报废">报废</option>
                      </Form.Select>
                      <LimsNavIcon name="expand_more" sizePx={22} className="ins-edit-modal__select-chevron" />
                    </div>
                  </Form.Group>
                </div>
                <Form.Group className="ins-edit-modal__field ins-edit-modal__field--full">
                  <Form.Label className="ins-edit-modal__label">负责人</Form.Label>
                  {isStockAdmin ? (
                    <div className="ins-edit-modal__multi">
                      <ResponsiblePersonMultiSelect
                        users={users}
                        selectedNames={selectedResponsiblePersons}
                        onChange={(nextSelectedNames) => {
                          setSelectedResponsiblePersons(nextSelectedNames);
                          setForm((prev) => ({ ...prev, responsiblePerson: nextSelectedNames.join('、') }));
                        }}
                        searchPlaceholder="搜索成员姓名/账号/角色"
                        helperText="可多选。已选负责人将作为该设备预约审批人（任一负责人可审批）。"
                      />
                    </div>
                  ) : (
                    <Form.Control
                      className="ins-edit-modal__control"
                      value={form.responsiblePerson || '—'}
                      readOnly
                      disabled
                    />
                  )}
                </Form.Group>
                <Form.Group className="ins-edit-modal__field ins-edit-modal__field--full">
                  <Form.Label className="ins-edit-modal__label">可用该设备成员</Form.Label>
                  <div className="ins-edit-modal__multi">
                    <ResponsiblePersonMultiSelect
                      users={users}
                      selectedNames={selectedAllowedUsers}
                      onChange={(nextSelectedNames) => {
                        setSelectedAllowedUsers(nextSelectedNames);
                        setForm((prev) => ({ ...prev, allowedUsers: nextSelectedNames.join('、') }));
                      }}
                      searchPlaceholder="搜索成员姓名/账号/角色"
                      helperText="可多选。未选择任何成员时，默认团队全部成员均可预约该设备。"
                      removeMemberLabel="可用成员"
                    />
                  </div>
                </Form.Group>
                <div className="ins-edit-modal__grid2">
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">购买日期</Form.Label>
                    <div className="ins-edit-modal__input-icon-wrap">
                      <LimsNavIcon name="calendar_today" sizePx={20} className="ins-edit-modal__input-icon" />
                      <Form.Control
                        className="ins-edit-modal__control ins-edit-modal__control--pl"
                        type="date"
                        value={form.purchaseDate || ''}
                        onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
                      />
                    </div>
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">保修到期</Form.Label>
                    <div className="ins-edit-modal__input-icon-wrap">
                      <LimsNavIcon name="verified_user" sizePx={20} className="ins-edit-modal__input-icon" />
                      <Form.Control
                        className="ins-edit-modal__control ins-edit-modal__control--pl"
                        type="date"
                        value={form.warrantyExpiry || ''}
                        onChange={(e) => setForm({ ...form, warrantyExpiry: e.target.value })}
                      />
                    </div>
                  </Form.Group>
                  <Form.Group className="ins-edit-modal__field">
                    <Form.Label className="ins-edit-modal__label">维护周期</Form.Label>
                    <div className="ins-edit-modal__select-wrap">
                      <Form.Select
                        className="ins-edit-modal__control ins-edit-modal__control--select"
                        value={maintenanceCycleMode}
                        onChange={(e) => {
                          const mode = e.target.value as 'none' | 'custom';
                          setMaintenanceCycleMode(mode);
                          if (mode === 'none') {
                            setForm((prev) => ({ ...prev, maintenanceCycleDays: null }));
                          } else {
                            const days = maintenanceCycleCustomDays > 0 ? maintenanceCycleCustomDays : 30;
                            setMaintenanceCycleCustomDays(days);
                            setForm((prev) => ({ ...prev, maintenanceCycleDays: days }));
                          }
                        }}
                      >
                        <option value="none">无</option>
                        <option value="custom">自定义天数</option>
                      </Form.Select>
                      <LimsNavIcon name="expand_more" sizePx={22} className="ins-edit-modal__select-chevron" />
                    </div>
                  </Form.Group>
                  {maintenanceCycleMode === 'custom' && (
                    <Form.Group className="ins-edit-modal__field">
                      <Form.Label className="ins-edit-modal__label">周期天数</Form.Label>
                      <Form.Control
                        className="ins-edit-modal__control"
                        type="number"
                        min={1}
                        max={3650}
                        value={maintenanceCycleCustomDays}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          const days = Number.isFinite(v) && v >= 1 ? v : 1;
                          setMaintenanceCycleCustomDays(days);
                          setForm((prev) => ({ ...prev, maintenanceCycleDays: days }));
                        }}
                      />
                      <Form.Text className="text-muted">
                        自保存当日起算；到期后系统将通知仪器负责人进行维护，无负责人时通知团队管理员。
                        {form.maintenanceCycleStartDate && form.maintenanceCycleDays
                          ? ` 当前周期预计到期：${formatMaintenanceDueDate(form.maintenanceCycleStartDate, form.maintenanceCycleDays)}。`
                          : ' 保存后开始新的维护周期。'}
                      </Form.Text>
                    </Form.Group>
                  )}
                </div>
              </section>

              <section className="ins-edit-modal__section">
                <div className="ins-edit-modal__section-head">
                  <span className="ins-edit-modal__section-bar" aria-hidden />
                  <h3 className="ins-edit-modal__section-title">图片与说明</h3>
                </div>
                <Form.Group className="ins-edit-modal__field">
                  <Form.Label className="ins-edit-modal__label">仪器图片</Form.Label>
                  <div className="ins-edit-modal__image-zone">
                    <img src={uploadPreview || DEFAULT_INSTRUMENT_IMAGE} alt="" className="ins-edit-modal__image-preview" />
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      className="ins-edit-modal__image-input"
                      onChange={handleImageChange}
                    />
                    <div className="ins-edit-modal__image-overlay">
                      <LimsNavIcon name="add_a_photo" sizePx={36} />
                      <span className="ins-edit-modal__image-cta">点击区域上传或更换图片</span>
                      <span className="ins-edit-modal__image-hint">支持 JPEG / PNG</span>
                    </div>
                  </div>
                </Form.Group>
                <Form.Group className="ins-edit-modal__field">
                  <Form.Label className="ins-edit-modal__label">描述</Form.Label>
                  <Form.Control
                    className="ins-edit-modal__control"
                    as="textarea"
                    rows={3}
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="简要说明仪器用途与特点…"
                  />
                </Form.Group>
                <Form.Group className="ins-edit-modal__field">
                  <Form.Label className="ins-edit-modal__label">使用说明（知识库文档）</Form.Label>
                  <p className="ins-edit-modal__help" style={{ marginTop: 0 }}>
                    从团队知识库中选择一篇文档作为该仪器的正式使用说明；成员在仪器详情与预约确认中可跳转阅读。
                  </p>
                  {(() => {
                    const kb = parseInstrumentUsageKbRef(form as Instrument);
                    return kb ? (
                      <div className="ins-edit-modal__kb-selected">
                        <span className="ins-edit-modal__kb-selected-title">{kb.title?.trim() || '已选知识库文档'}</span>
                        <span className="ins-edit-modal__kb-selected-id text-muted small">ID: {kb.id}</span>
                      </div>
                    ) : (
                      <p className="text-muted small mb-2">当前未关联知识库文档。</p>
                    );
                  })()}
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <button
                      type="button"
                      className="ins-edit-modal__btn ins-edit-modal__btn--ghost"
                      onClick={() => {
                        setUsageKbPickerOpen((o) => !o);
                        if (!usageKbPickerOpen) void fetchUsageKbOptions('');
                      }}
                    >
                      <LimsNavIcon name="menu_book" sizePx={18} />
                      {usageKbPickerOpen ? '收起列表' : '选择知识库文档'}
                    </button>
                    <button
                      type="button"
                      className="ins-edit-modal__btn ins-edit-modal__btn--ghost"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          usageInstructionKnowledgeBaseId: undefined
                        }))
                      }
                    >
                      清除关联
                    </button>
                  </div>
                  {usageKbPickerOpen ? (
                    <div className="ins-edit-modal__kb-picker">
                      <Form.Control
                        className="ins-edit-modal__control mb-2"
                        type="search"
                        placeholder="按标题搜索知识库…"
                        value={usageKbKeyword}
                        onChange={(e) => setUsageKbKeyword(e.target.value)}
                        autoComplete="off"
                      />
                      {usageKbLoading ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : usageKbList.length === 0 ? (
                        <p className="text-muted small mb-0">没有可选择的文档，请先在「知识库」中创建并确保您有权查看。</p>
                      ) : (
                        <ul className="ins-edit-modal__kb-list list-unstyled mb-0">
                          {usageKbList.map((doc) => (
                            <li key={doc._id}>
                              <button
                                type="button"
                                className="ins-edit-modal__kb-list-item"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    usageInstructionKnowledgeBaseId: doc._id,
                                    usageInstructions: ''
                                  }));
                                  setUsageKbPickerOpen(false);
                                }}
                              >
                                <span className="ins-edit-modal__kb-list-title">{doc.title}</span>
                                {doc.status && doc.status !== 'published' ? (
                                  <span className="badge bg-secondary ms-2">{doc.status}</span>
                                ) : null}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </Form.Group>
              </section>

              <section className="ins-edit-modal__section ins-edit-modal__section--rules">
                <div className="ins-edit-modal__section-head ins-edit-modal__section-head--rules">
                  <span className="ins-edit-modal__section-bar ins-edit-modal__section-bar--secondary" aria-hidden />
                  <h3 className="ins-edit-modal__section-title ins-edit-modal__section-title--secondary">预约规则</h3>
                </div>
                <div className="ins-edit-modal__rules-inner">
                  <div className="ins-edit-modal__rules-row">
                    <span className="ins-edit-modal__rules-subtitle">时间限制</span>
                    <button
                      type="button"
                      className={`ins-edit-modal__chip-toggle${useDefaultTimeSlots ? ' is-on' : ''}`}
                      onClick={() => setUseDefaultTimeSlots((prev) => !prev)}
                    >
                      使用默认时段
                    </button>
                  </div>
                  <div className="ins-edit-modal__grid3">
                    <Form.Group className="ins-edit-modal__field">
                      <Form.Label className="ins-edit-modal__label">单次最长（分钟）</Form.Label>
                      <Form.Control
                        className="ins-edit-modal__control"
                        type="number"
                        value={form.reservationRules?.maxDuration || 240}
                        onChange={(e) => updateReservationRule('maxDuration', parseInt(e.target.value, 10))}
                      />
                    </Form.Group>
                    <Form.Group className="ins-edit-modal__field">
                      <Form.Label className="ins-edit-modal__label">单次最短（分钟）</Form.Label>
                      <Form.Control
                        className="ins-edit-modal__control"
                        type="number"
                        value={form.reservationRules?.minDuration || 30}
                        onChange={(e) => updateReservationRule('minDuration', parseInt(e.target.value, 10))}
                      />
                    </Form.Group>
                    <Form.Group className="ins-edit-modal__field">
                      <Form.Label className="ins-edit-modal__label">提前预约（天）</Form.Label>
                      <Form.Control
                        className="ins-edit-modal__control"
                        type="number"
                        value={form.reservationRules?.advanceBookingDays || 7}
                        onChange={(e) => updateReservationRule('advanceBookingDays', parseInt(e.target.value, 10))}
                      />
                    </Form.Group>
                  </div>

                  <div className="ins-edit-modal__rules-block">
                    <h4 className="ins-edit-modal__rules-h4">可预约时间段</h4>
                    {useDefaultTimeSlots && (
                      <div className="ins-edit-modal__hint ins-edit-modal__hint--ok">
                        使用默认规则：每天 07:00 - 23:00（下方每日设置已禁用）
                      </div>
                    )}
                    <div className={`ins-edit-modal__slots${useDefaultTimeSlots ? ' is-muted' : ''}`}>
                      {(form.reservationRules?.availableTimeSlots || [])
                        .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                        .map((slot) => (
                          <div key={slot.dayOfWeek} className="ins-edit-modal__slot-card">
                            <div className="ins-edit-modal__slot-head">
                              <Form.Check
                                type="checkbox"
                                checked={slot.isEnabled}
                                onChange={(e) => updateTimeSlot(slot.dayOfWeek, 'isEnabled', e.target.checked)}
                                disabled={useDefaultTimeSlots}
                                id={`ins-slot-${slot.dayOfWeek}`}
                              />
                              <Form.Label className="ins-edit-modal__slot-day" htmlFor={`ins-slot-${slot.dayOfWeek}`}>
                                {getDayName(slot.dayOfWeek)}
                              </Form.Label>
                            </div>
                            <div className="ins-edit-modal__slot-times">
                              <div>
                                <Form.Label className="ins-edit-modal__slot-mini">开始</Form.Label>
                                <Form.Control
                                  className="ins-edit-modal__control ins-edit-modal__control--sm"
                                  type="time"
                                  value={slot.startTime}
                                  onChange={(e) => updateTimeSlot(slot.dayOfWeek, 'startTime', e.target.value)}
                                  disabled={!slot.isEnabled || useDefaultTimeSlots}
                                  size="sm"
                                />
                              </div>
                              <div>
                                <Form.Label className="ins-edit-modal__slot-mini">结束</Form.Label>
                                <Form.Control
                                  className="ins-edit-modal__control ins-edit-modal__control--sm"
                                  type="time"
                                  value={slot.endTime}
                                  onChange={(e) => updateTimeSlot(slot.dayOfWeek, 'endTime', e.target.value)}
                                  disabled={!slot.isEnabled || useDefaultTimeSlots}
                                  size="sm"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="ins-edit-modal__rules-block">
                    <h4 className="ins-edit-modal__rules-h4">结束使用状态</h4>
                    <Form.Check
                      type="switch"
                      className="ins-edit-modal__check mb-3"
                      label="启用结束使用状态（使用后需提交照片与文字说明）"
                      checked={Boolean(form.reservationRules?.endUseStatusEnabled)}
                      onChange={(e) => {
                        const on = e.target.checked;
                        updateReservationRule('endUseStatusEnabled', on);
                        if (!on) updateReservationRule('endUsePhotoGroups', []);
                      }}
                    />
                    {form.reservationRules?.endUseStatusEnabled && (
                      <>
                        <p className="ins-edit-modal__help mb-2">
                          自定义必填照片组（如氧气浓度、整洁度等）。预约人在结束使用后需按组上传照片。
                        </p>
                        {(form.reservationRules.endUsePhotoGroups || []).map((group, index) => (
                          <div
                            key={group.key || index}
                            className="d-flex flex-wrap gap-2 align-items-end mb-2 p-2 border rounded"
                          >
                            <Form.Group className="flex-grow-1" style={{ minWidth: 140 }}>
                              <Form.Label className="small mb-1">照片组名称</Form.Label>
                              <Form.Control
                                size="sm"
                                value={group.label}
                                placeholder="如：氧气浓度"
                                onChange={(e) => updateEndUsePhotoGroup(index, { label: e.target.value })}
                              />
                            </Form.Group>
                            <Form.Group style={{ width: 88 }}>
                              <Form.Label className="small mb-1">最多张数</Form.Label>
                              <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={10}
                                value={group.maxPhotos ?? 3}
                                onChange={(e) =>
                                  updateEndUsePhotoGroup(index, {
                                    maxPhotos: parseInt(e.target.value, 10) || 3
                                  })
                                }
                              />
                            </Form.Group>
                            <Form.Check
                              type="checkbox"
                              className="mb-2"
                              label="必填"
                              checked={group.required !== false}
                              onChange={(e) => updateEndUsePhotoGroup(index, { required: e.target.checked })}
                            />
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => removeEndUsePhotoGroup(index)}
                            >
                              删除
                            </Button>
                          </div>
                        ))}
                        <Button size="sm" variant="outline-primary" type="button" onClick={addEndUsePhotoGroup}>
                          + 添加照片组
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="ins-edit-modal__rules-block">
                    <h4 className="ins-edit-modal__rules-h4">审批与并发</h4>
                    <div className="ins-edit-modal__grid2">
                      <Form.Group className="ins-edit-modal__field">
                        <Form.Check
                          type="checkbox"
                          className="ins-edit-modal__check"
                          label="需要审批（由设备负责人审批）"
                          checked={Boolean(form.reservationRules?.requiresApproval)}
                          onChange={(e) => updateReservationRule('requiresApproval', e.target.checked)}
                        />
                        <p className="ins-edit-modal__help">关闭后，该设备预约将自动通过（即使已配置负责人）</p>
                      </Form.Group>
                      <Form.Group className="ins-edit-modal__field">
                        <Form.Label className="ins-edit-modal__label">最大同时使用人数</Form.Label>
                        <Form.Control
                          className="ins-edit-modal__control"
                          type="number"
                          min={1}
                          value={form.reservationRules?.maxConcurrentUsers || 1}
                          onChange={(e) => updateReservationRule('maxConcurrentUsers', parseInt(e.target.value, 10))}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </section>
            </Form>
          </div>

          <footer className="ins-edit-modal__footer">
            <button type="button" className="ins-edit-modal__discard" onClick={handleDiscardInstrumentForm}>
              <LimsNavIcon name="delete" sizePx={20} />
              放弃修改
            </button>
            <div className="ins-edit-modal__footer-actions">
              <button type="button" className="ins-edit-modal__btn ins-edit-modal__btn--ghost" onClick={() => setShow(false)}>
                取消
              </button>
              <button type="button" className="ins-edit-modal__btn ins-edit-modal__btn--primary" onClick={handleSave}>
                <LimsNavIcon name="save" sizePx={20} />
                保存仪器
              </button>
            </div>
          </footer>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default InstrumentStock; 